import {
  formatCompactMoney,
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
  formatPercent,
  formatQuantity,
  todayISO,
  type DecimalLike,
} from '@easiibill/shared';
import type { BusinessDto } from '@easiibill/types';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { api } from '@/lib/api';
import { useWorkspace } from '@/lib/workspace';

export function useBusinessQuery() {
  const businessId = useWorkspace((s) => s.businessId);
  return useQuery({
    queryKey: ['b', businessId, 'business'],
    queryFn: () => api<BusinessDto>('/api/business'),
    enabled: Boolean(businessId),
    staleTime: 60_000,
  });
}

const BusinessContext = createContext<BusinessDto | null>(null);

export function BusinessProvider({ business, children }: { business: BusinessDto; children: ReactNode }) {
  return <BusinessContext.Provider value={business}>{children}</BusinessContext.Provider>;
}

export function useBusiness(): BusinessDto {
  const business = useContext(BusinessContext);
  if (!business) throw new Error('useBusiness must be used inside the app shell');
  return business;
}

export function useOptionalBusiness(): BusinessDto | null {
  return useContext(BusinessContext);
}

export function useCanAdmin(): boolean {
  const business = useBusiness();
  return business.role === 'owner' || business.role === 'admin';
}

export interface Formatters {
  money: (value: DecimalLike | null | undefined, currency?: string) => string;
  compact: (value: DecimalLike | null | undefined, currency?: string) => string;
  number: (value: DecimalLike, decimals?: number) => string;
  quantity: (value: DecimalLike) => string;
  percent: (value: DecimalLike) => string;
  date: (value: string | null | undefined) => string;
  dateTime: (value: string | null | undefined) => string;
  today: () => string;
}

export function createFormatters(business: Pick<BusinessDto, 'currency' | 'settings'> | null): Formatters {
  const settings = business?.settings;
  const format = settings?.numberFormat ?? 'en';
  const display = settings?.currencyDisplay ?? 'symbol';
  const currency = business?.currency ?? 'USD';
  const timezone = settings?.timezone ?? 'UTC';
  return {
    money: (value, code = currency) => formatMoney(value ?? '0', code, { format, display }),
    compact: (value, code = currency) => formatCompactMoney(value ?? '0', code, format),
    number: (value, decimals = 0) => formatNumber(value, decimals, { format }),
    quantity: (value) => formatQuantity(value, format),
    percent: (value) => formatPercent(value, format),
    date: (value) => (value ? formatDate(value, settings?.dateFormat ?? 'DD MMM YYYY') : '—'),
    dateTime: (value) => (value ? formatDateTime(value, timezone) : '—'),
    today: () => todayISO(timezone),
  };
}

export function useFormat(): Formatters {
  const business = useOptionalBusiness();
  return useMemo(() => createFormatters(business), [business]);
}
